// M16 Folie gestalten: Vorlage mit Feldformular – Zonenvorlage (specVersion 1) oder freie Folie mit Feldern
// (specVersion 2, z. B. Branchenvorlagen) –, Vorschau mit hervorgehobener Zone, Zeichenzähler,
// Vorlagenwechsel (gleichnamige Felder bleiben erhalten).
import {
  fillTargets, parseScheduleText, scheduleToText, validateContent, type LayoutElement, type ZoneContent, type ZoneContentMap,
} from '@disploraq/layouts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { can, qk, useLayouts, useMe, usePlaylist } from '../api/queries';
import type { LayoutRow, PlaylistDetail } from '../api/types';
import { Brand } from '../components/Brand';
import { BindingEditor, BindingSwitch, SourcePicker } from '../components/DataBinding';
import { Icon } from '../components/Icon';
import { LayoutPreview, mediaUrl, useMediaIndex } from '../components/Preview';
import { useToast } from '../components/toast';
import { Spinner } from '../components/ui';
import { toDraft, toInput } from './PlaylistEditor';
import './templates/gallery.css';
import { fillable } from './templates/shared';
import { studioPath, useEditCopy } from '../api/template-edit';

type Row = { time?: string; course?: string; trainer?: string; room?: string };

export function SlideDesignPage() {
  const { id = '', itemId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('en') ? 'en' : 'de';
  const { data: me } = useMe();
  const { data: pl } = usePlaylist(id);
  const { data: layouts } = useLayouts();
  const media = useMediaIndex();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const editCopy = useEditCopy();
  const item = pl?.items.find((x) => x.id === itemId);
  const [layoutId, setLayoutId] = useState<string | null>(null);
  const [content, setContent] = useState<ZoneContentMap | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (item && content === null) {
      setLayoutId(item.layoutId);
      setContent((item.layoutContent ?? {}) as ZoneContentMap);
      setDuration(item.durationMs);
    }
  }, [item, content]);

  const layout = layouts?.find((l) => l.id === layoutId);
  const def = fillable(layout);
  const sameOrientation = useMemo(() => (layouts ?? []).filter((l) => {
    const d = fillable(l);
    if (!d || !def || d.specVersion !== def.specVersion || l.orientation !== layout?.orientation || l.latestRevisionNo === null) return false;
    // freie Folien: nur eigene Vorlagen (Branchenvorlagen werden über die Galerie kopiert)
    return d.specVersion === 1 || l.scope === 'tenant';
  }), [layouts, layout?.orientation, def]);
  const problems = def && content ? validateContent(def, content) : [];

  if (!pl || !layouts || content === null) return <div className="content"><Spinner /></div>;
  if (!item) return <div className="content card muted">{t('common.notFound')} <Link to={`/playlists/${id}`}>{t('common.back')}</Link></div>;
  if (!def) {
    return <div className="content card col">{t('design.notTemplate')}<div><Link className="btn" to={`/playlists/${id}`}>{t('common.back')}</Link></div></div>;
  }

  const set = (zone: string, c: ZoneContent | null) => {
    setTouched(true);
    setContent((cur) => {
      const n = { ...(cur ?? {}) };
      if (c === null) delete n[zone];
      else n[zone] = c;
      return n;
    });
  };
  const switchTemplate = (l: LayoutRow) => {
    const d = fillable(l);
    if (!d) return;
    const zones = new Set(fillTargets(d).map((z) => z.id));
    setContent((cur) => Object.fromEntries(Object.entries(cur ?? {}).filter(([k]) => zones.has(k))));
    setLayoutId(l.id);
    setTouched(true);
  };

  /** Eingaben in der Playlist speichern; liefert die neue id der Folie. */
  const persist = async (): Promise<string> => {
    const fresh = await api.get<PlaylistDetail>(`/playlists/${id}`);
    const idx = fresh.items.findIndex((x) => x.id === itemId);
    if (idx < 0) throw new Error(t('common.notFound'));
    const items = fresh.items.map(toDraft).map((x, i) => (i === idx ? { ...x, layoutId, layoutContent: content, durationMs: duration } : x));
    const res = await api.put<PlaylistDetail>(`/playlists/${id}/items`, { items: items.map(toInput) }, { ifMatch: fresh.rowVersion });
    qc.setQueryData([...qk.playlist(id), me?.activeTenantId ?? 'none'], res);
    void qc.invalidateQueries({ queryKey: qk.playlists });
    setTouched(false);
    return res.items[idx]!.id;
  };
  // „Frei gestalten": Eingaben speichern, Folie auf eine eigene Kopie mit diesen Werten umhängen, Studio öffnen
  const freeDesign = async () => {
    setBusy(true);
    try {
      const newItemId = await persist();
      const r = await editCopy(layoutId!, { playlistId: id, itemId: newItemId });
      toast.show(t('tedit.freeDone', { name: r.layout.name }));
      navigate(studioPath(r.layout.id, `/playlists/${id}`));
    } catch (e) {
      toast.error(e);
      setBusy(false);
    }
  };

  const save = async (close: boolean) => {
    setBusy(true);
    try {
      const newItemId = await persist();
      toast.show(t('common.saved'));
      if (close) navigate(`/playlists/${id}`);
      else navigate(`/playlists/${id}/items/${newItemId}/design`, { replace: true });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const errMsg = (m: string) => (m === 'Pflichtfeld fehlt' ? t('design.required') : m.startsWith('Höchstens') ? t('design.tooLong') : m);
  const images = media.list.filter((m) => m.kind === 'image' && m.current?.status === 'ready');
  const any = media.list.filter((m) => m.current?.status === 'ready');
  const W = def.specVersion === 1 ? def.refWidth : layout!.refWidth;
  const H = def.specVersion === 1 ? def.refHeight : layout!.refHeight;
  const targets = fillTargets(def);
  const elById = new Map<string, LayoutElement>(def.specVersion === 2 ? def.elements.flatMap((e) => (e.id ? [[e.id, e] as const] : [])) : []);
  const videos = media.list.filter((m) => m.kind === 'video' && m.current?.status === 'ready');

  return (
    <div className="designer">
      <div className="d-top">
        <Brand tag={false} />
        <b className="grow" style={{ minWidth: 120 }}>{t('design.title', { name: pl.name })}</b>
        <span className="pill neutral plain">{layout!.orientation === 'portrait' ? t('design.portrait') : t('design.landscape')}</span>
        <label className="row muted small" style={{ gap: 6 }}><Icon name="clock" />
          <input type="number" min={1} style={{ width: 72 }} aria-label={t('playlists.durationS')} placeholder={String(pl.defaultDurationMs / 1000)}
            value={duration ? duration / 1000 : ''} onChange={(e) => { setTouched(true); setDuration(e.target.value ? Math.max(1, Number(e.target.value)) * 1000 : null); }} /> s
        </label>
        <button className="btn ghost" onClick={() => (!touched || window.confirm(t('design.discard'))) && navigate(`/playlists/${id}`)}>{t('common.cancel')}</button>
        <button className="btn" disabled={busy} onClick={() => void save(false)}>{t('common.save')}</button>
        <button className="btn primary" disabled={busy} onClick={() => void save(true)}>{t('design.apply')}</button>
      </div>
      <div className="d-body">
        <div className="d-tools">
          <div className="lbl">{t('design.template')}</div>
          {sameOrientation.map((l) => (
            <button key={l.id} onClick={() => switchTemplate(l)} aria-pressed={l.id === layoutId} style={l.id === layoutId ? { background: 'var(--accent-soft)', color: 'var(--accent)' } : undefined}>
              <Icon name="layout" />{l.name}
            </button>
          ))}
          <div className="lbl tip" style={{ marginTop: 8 }}>{t('design.tip')}</div>
          <div className="muted small tip" style={{ padding: '0 8px' }}>{t('design.tipText')}</div>
        </div>
        <div className="d-stage">
          <div className={`d-frame ${layout!.orientation}`}>
            <LayoutPreview definition={def} content={content} media={media} />
            <div className="d-overlay" style={{ pointerEvents: 'none' }}>
              {targets.filter((z) => def.fields?.some((f) => f.zone === z.id)).map((z) => (
                <div key={z.id} className={`d-zone${hover === z.id ? ' hl' : ''}`}
                  style={{ left: `${z.x}%`, top: `${z.y}%`, width: `${z.w}%`, height: `${z.h}%` }} />
              ))}
            </div>
          </div>
        </div>
        <div className="d-props">
          <h3 style={{ margin: 0 }}>{layout!.name}</h3>
          <div className="hint">{t('design.aspectHint', { ratio: layout!.orientation === 'portrait' ? '9:16' : '16:9', w: W, h: H })}</div>
          {can(me, 'layouts.manage') && (
            <div className="col" style={{ gap: 4 }}>
              <button className="btn primary" disabled={busy} onClick={() => void freeDesign()} data-testid="free-design"><Icon name="edit" />{t('tedit.freeDesign')}</button>
              <span className="small muted">{t('tedit.freeDesignHint')}</span>
            </div>
          )}
          {def.specVersion === 2 && layout!.scope === 'tenant' && can(me, 'layouts.manage') && (
            <Link className="btn sm" to={`/studio/${layout!.id}?back=${encodeURIComponent(`/playlists/${id}/items/${itemId}/design`)}`}
              onClick={(e) => { if (touched && !window.confirm(t('design.discard'))) e.preventDefault(); }}>
              <Icon name="edit" />{t('gallery.designInStudio')}
            </Link>
          )}
          {(def.fields ?? []).map((f) => {
            const zone = targets.find((z) => z.id === f.zone);
            if (!zone) return null;
            const el = elById.get(f.zone);
            const c = content[f.zone];
            const label = `${f.label[lang]}${f.required ? ' *' : ''}`;
            const err = problems.find((p) => p.zone === f.zone);
            const common = { onFocus: () => setHover(f.zone), onBlur: () => setHover(null), onMouseEnter: () => setHover(f.zone), onMouseLeave: () => setHover(null) };
            // Feldbelegung aus einer Datenquelle (Tabelle bzw. Feed)
            if ((zone.kind === 'rows' || zone.kind === 'text') && c?.type === 'data') {
              return <div key={f.zone} className="field" {...common}>{label}<BindingEditor kind={zone.kind} value={c} onChange={(b) => set(f.zone, b)} /></div>;
            }
            if (zone.kind === 'data') {
              const v = c?.type === 'data' ? c.source : '';
              return (
                <label key={f.zone} className="field" {...common}>{label}
                  <SourcePicker kind={zone.type as 'feed' | 'calendar' | 'table'} value={v} onChange={(src) => set(f.zone, src ? { type: 'data', source: src } : null)} />
                </label>
              );
            }
            if (zone.kind === 'rows') {
              const rows: Row[] = c?.type === 'text' ? parseScheduleText(c.value) : ((el as { rows?: Row[] } | undefined)?.rows ?? []);
              const put = (next: Row[]) => set(f.zone, { type: 'text', value: scheduleToText(next) });
              const upd = (i: number, p: Row) => put(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
              return (
                <div key={f.zone} className="field" {...common}>{label}
                  <div className="rows4">
                    {rows.map((r, i) => (
                      <div key={i} className="r">
                        <input aria-label={t('studio.f.time')} placeholder={t('studio.f.time')} value={r.time ?? ''} onChange={(e) => upd(i, { time: e.target.value })} />
                        <input aria-label={t('studio.f.course')} placeholder={t('studio.f.course')} value={r.course ?? ''} onChange={(e) => upd(i, { course: e.target.value })} />
                        <input aria-label={t('gallery.trainer')} placeholder={t('gallery.trainer')} value={r.trainer ?? ''} onChange={(e) => upd(i, { trainer: e.target.value })} />
                        <input aria-label={t('gallery.room')} placeholder={t('gallery.room')} value={r.room ?? ''} onChange={(e) => upd(i, { room: e.target.value })} />
                        <button type="button" className="btn sm ghost icon" aria-label={t('common.remove')} onClick={() => put(rows.filter((_, j) => j !== i))}><Icon name="x" /></button>
                      </div>
                    ))}
                    <button type="button" className="btn sm" onClick={() => put([...rows, { time: '', course: '' }])}><Icon name="plus" />{t('studio.addRow')}</button>
                  </div>
                  <BindingSwitch kind="rows" onBind={(b) => set(f.zone, b)} />
                </div>
              );
            }
            if (zone.kind === 'text') {
              // Freie Folien: Vorgabetext der Vorlage steht im Feld; geleert bleibt das Feld leer
              const v = c?.type === 'text' ? c.value : el && 'text' in el ? (el.text ?? '') : '';
              const over = f.maxLength !== undefined && v.length > f.maxLength;
              return (
                <label key={f.zone} className="field" {...common}>{label}
                  <textarea rows={2} value={v} aria-invalid={over || !!err}
                    onChange={(e) => set(f.zone, e.target.value || el ? { type: 'text', value: e.target.value } : null)} />
                  {f.maxLength !== undefined && <span className={`counter${over ? ' over' : ''}`}>{v.length} / {f.maxLength}</span>}
                  {err && touched && <span className="field-error">{errMsg(err.message)}</span>}
                  <BindingSwitch kind="text" onBind={(b) => set(f.zone, b)} />
                </label>
              );
            }
            if (zone.kind === 'playlist') {
              const sel = c?.type === 'playlist' ? c.items.map((x) => x.asset) : [];
              return (
                <div key={f.zone} className="field" {...common}>{label}
                  <div className="pick">
                    {images.map((m) => (
                      <label key={m.id} title={m.name}>
                        <input type="checkbox" checked={sel.includes(m.current!.sha256)} onChange={(e) => {
                          const next = e.target.checked ? [...sel, m.current!.sha256] : sel.filter((x) => x !== m.current!.sha256);
                          set(f.zone, next.length ? { type: 'playlist', items: next.map((asset) => ({ asset })) } : null);
                        }} />
                        <img src={mediaUrl(m)} alt={m.name} />
                      </label>
                    ))}
                  </div>
                  {!images.length && <span className="muted small">{t('media.empty')}</span>}
                  {err && touched && <span className="field-error">{errMsg(err.message)}</span>}
                </div>
              );
            }
            if (zone.kind === 'media' || zone.kind === 'logo') {
              const v = c?.type === 'media' ? c.asset : '';
              const list = zone.kind === 'logo' || zone.type === 'image' ? images : zone.type === 'video' ? videos : any;
              return (
                <label key={f.zone} className="field" {...common}>{label}
                  <select value={v} onChange={(e) => set(f.zone, e.target.value ? { type: 'media', asset: e.target.value } : null)} aria-invalid={!!err}>
                    <option value="">{el ? t('gallery.placeholderImage') : t('design.choose')}</option>
                    {list.map((m) => <option key={m.id} value={m.current!.sha256}>{m.name} ({m.kind === 'video' ? t('media.video') : t('media.image')})</option>)}
                  </select>
                  {err && touched && <span className="field-error">{errMsg(err.message)}</span>}
                </label>
              );
            }
            return null;
          })}
          {!def.fields?.length && <div className="muted small">{t('design.noFields')}</div>}
          {problems.length > 0 && <div className="hint warn">{t('design.missing', { count: problems.length })}</div>}
        </div>
      </div>
    </div>
  );
}
